import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatDate } from '../utils/date';

const STATUS_BADGES = { pending: 'badge-yellow', approved: 'badge-green', rejected: 'badge-red' };
const LEAVE_TYPES = ['Casual Leave', 'Sick Leave', 'Earned Leave', 'Comp Off', 'Loss of Pay', 'Maternity Leave', 'Paternity Leave'];

export default function Leave() {
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyModal, setApplyModal] = useState(false);
  const [form, setForm] = useState({ leaveType: 'Casual Leave', fromDate: '', toDate: '', days: '', reason: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const { dirty, markDirty, resetDirty } = useFormDirty();

  const fetchBalances = useCallback(async () => {
    try {
      const res = await apiFetch('/api/leave/balances?mine=true');
      if (res.ok) {
        const data = await res.json();
        setBalances(data.balances || []);
      }
    } catch (err) { /* handled */ }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      let url = '/api/leave/requests';
      if (filterStatus) url += `?status=${filterStatus}`;
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, [filterStatus]);

  useEffect(() => { fetchBalances(); fetchRequests(); }, [fetchBalances, fetchRequests]);

  function handleField(name, value) {
    setForm(prev => ({ ...prev, [name]: value }));
    markDirty();
  }

  async function handleApply() {
    const errs = {};
    if (!form.fromDate) errs.fromDate = 'Required';
    if (!form.toDate) errs.toDate = 'Required';
    if (!form.days) errs.days = 'Required';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSaving(true);
    try {
      const res = await apiFetch('/api/leave/request', { method: 'POST', body: JSON.stringify(form) });
      if (res.ok) {
        resetDirty();
        setApplyModal(false);
        setForm({ leaveType: 'Casual Leave', fromDate: '', toDate: '', days: '', reason: '' });
        fetchRequests();
        fetchBalances();
      } else {
        const data = await res.json();
        setErrors({ _general: data.error || 'Failed to apply' });
      }
    } catch (err) {
      setErrors({ _general: 'Network error' });
    }
    setSaving(false);
  }

  async function handleApprove(reqId) {
    try {
      await apiFetch(`/api/leave/request/${reqId}/approve`, { method: 'POST' });
      fetchRequests();
      fetchBalances();
    } catch (err) { /* ignore */ }
  }

  async function handleReject(reqId) {
    try {
      await apiFetch(`/api/leave/request/${reqId}/reject`, { method: 'POST' });
      fetchRequests();
    } catch (err) { /* ignore */ }
  }

  // Calculate days automatically
  function autoCalcDays(from, to) {
    if (from && to) {
      const f = new Date(from);
      const t = new Date(to);
      const diff = Math.ceil((t - f) / (1000 * 60 * 60 * 24)) + 1;
      if (diff > 0) {
        setForm(prev => ({ ...prev, days: diff.toString() }));
        markDirty();
      }
    }
  }

  return (
    <div>
      {/* Leave Balance Cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        {balances.length > 0 ? balances.map(b => (
          <div key={b.id} className="card" style={{ flex: '1 0 180px', maxWidth: 240 }}>
            <div className="card-body" style={{ padding: 16 }}>
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>{b.leave_type}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 24, fontWeight: 600, color: '#1E293B' }}>{b.balance || 0}</span>
                <span style={{ fontSize: 12, color: '#94A3B8' }}>of {b.entitled || 0}</span>
              </div>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Taken: {b.taken || 0}</div>
            </div>
          </div>
        )) : (
          <div style={{ color: '#94A3B8', padding: 20 }}>No leave balances configured</div>
        )}
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="form-input" style={{ width: 160 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <button className="btn btn-primary" onClick={() => { resetDirty(); setApplyModal(true); }}>+ Apply Leave</button>
      </div>

      {/* Leave Requests Table */}
      <div className="card">
        <div className="card-header"><h2>Leave Requests</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner"></div></div>
          ) : requests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No leave requests found</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Reason</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => (
                  <tr key={r.id}>
                    <td>{r.first_name ? `${r.first_name} ${r.last_name}` : (r.emp_code || '--')}</td>
                    <td>{r.leave_type}</td>
                    <td>{formatDate(r.from_date, '--')}</td>
                    <td>{formatDate(r.to_date, '--')}</td>
                    <td>{r.days}</td>
                    <td>{r.reason || '--'}</td>
                    <td><span className={`badge ${STATUS_BADGES[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                    <td className="actions-cell">
                      {r.status === 'pending' && (
                        <>
                          <button className="btn btn-primary" style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => handleApprove(r.id)}>Approve</button>
                          <button className="btn btn-secondary" style={{ fontSize: 11, padding: '2px 8px' }}
                            onClick={() => handleReject(r.id)}>Reject</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Apply Leave Modal */}
      <Modal title="Apply for Leave" isOpen={applyModal} onClose={() => setApplyModal(false)} size="md" dirty={dirty}>
        {errors._general && (
          <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
            {errors._general}
          </div>
        )}
        <FormField label="Leave Type" name="leaveType" type="select" value={form.leaveType} onChange={handleField} options={LEAVE_TYPES} />
        <div className="form-row">
          <FormField label="From Date" name="fromDate" type="date" value={form.fromDate} onChange={(n, v) => {
            handleField(n, v);
            autoCalcDays(v, form.toDate);
          }} error={errors.fromDate} required />
          <FormField label="To Date" name="toDate" type="date" value={form.toDate} onChange={(n, v) => {
            handleField(n, v);
            autoCalcDays(form.fromDate, v);
          }} error={errors.toDate} required />
        </div>
        <FormField label="Number of Days" name="days" type="number" value={form.days} onChange={handleField} error={errors.days} required />
        <FormField label="Reason" name="reason" type="textarea" value={form.reason} onChange={handleField} />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setApplyModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleApply} disabled={saving}>
            {saving ? 'Submitting...' : 'Apply Leave'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
